import { AI_CHAT_API_URL, AI_API_URL, AI_MODEL } from '@api/constant';
import { HOST } from '@api/constant';
import { noMonitor } from '@api/api';

// Interface for chat message structure
export interface ChatMessage {
	role: 'user' | 'assistant' | 'system';
	content: string;
	timestamp?: Date;
}

// Interface for API response
interface AIResponse {
	response?: string;
	error?: string;
	success?: boolean;
	// Add other possible response fields from your backend
	query?: string;
	context?: string;
	results?: any[];
}

// Function to format chat history for Ollama
function formatHistoryForOllama(history: ChatMessage[]): ChatMessage[] {
	// Keep only the last 10 messages to avoid token limits
	const recentHistory = history.slice(-10);

	// Ensure we have a proper conversation flow
	const formattedHistory: ChatMessage[] = [];

	for (const message of recentHistory) {
		if (message.role === 'user' || message.role === 'assistant') {
			formattedHistory.push({
				role: message.role,
				content: message.content.trim(),
			});
		}
	}

	return formattedHistory;
}

export default async function handleHelp(
	editorConnected: boolean,
	query: string,
	history: ChatMessage[] = [],
	actionSchemas: any[] = [],
	mode: string = 'ask',
	imageAnalysis?: any,
	imageData?: string
): Promise<AIResponse | Response> {
	try {
		// Reduced logging for better performance - only log in development
		if (__DEV__) {
			console.log('🔧 handleHelp - History Length:', history.length);
		}

		// Format the conversation history for context with performance optimization
		const formattedHistory = formatHistoryForOllama(history || []);

		// Ensure we have valid arrays (never null/undefined)
		const conversationHistory = Array.isArray(formattedHistory)
			? formattedHistory
					.filter((msg) => msg && msg.role && msg.content) // Filter out invalid messages
					.map((msg) => ({
						role: msg.role,
						content: String(msg.content || '').trim(),
					}))
					.filter((msg) => msg.content.length > 0) // Remove empty messages
			: [];

		// Create the payload with conversation context matching your backend API
		const requestPayload: any = {
			query: String(query || '').trim(),
			conversation_history: conversationHistory, // Always an array, never null/undefined
			context: editorConnected
				? 'User is working in a content editor and needs response formatted for insertion'
				: 'General help request',
			max_results: 5,
			stream: true, // Enable streaming responses
			mode: mode || 'ask', // Include mode for plan mode support
		};

		// Only include action_schemas if we have valid schemas (don't send undefined/null)
		if (
			actionSchemas &&
			Array.isArray(actionSchemas) &&
			actionSchemas.length > 0
		) {
			// Ensure all schemas are valid objects
			const validSchemas = actionSchemas.filter(
				(schema) =>
					schema && typeof schema === 'object' && schema.intent
			);
			if (validSchemas.length > 0) {
				requestPayload.action_schemas = validSchemas;
			}
		}

		// Include image data if available (Easy Mode image upload)
		// Backend will analyze the image if image_data is provided but image_analysis is not
		if (imageData) {
			requestPayload.image_data = imageData;
			// Only include analysis if it was already done (frontend analysis)
			if (imageAnalysis) {
				requestPayload.image_analysis = imageAnalysis;
			}
		}

		if (!editorConnected) {
			// For general help queries - use fetch for streaming support
			try {
				// Use fetch for streaming support (noMonitor doesn't support streaming)
				if (__DEV__) {
					console.log('📤 [handleHelp] Sending request to:', `${HOST}/api/ai/help`);
					console.log('📤 [handleHelp] Request payload:', {
						query: requestPayload.query,
						historyLength: requestPayload.conversation_history?.length || 0,
						hasSchemas: !!requestPayload.action_schemas,
					});
				}

				const response = await fetch(`${HOST}/api/ai/help`, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include', // Include cookies for authentication
					body: JSON.stringify(requestPayload),
				});

				if (__DEV__) {
					console.log('📥 [handleHelp] Response received:', {
						status: response.status,
						statusText: response.statusText,
						ok: response.ok,
						contentType: response.headers.get('content-type'),
						hasBody: !!response.body,
					});
				}

				// Check if response is OK
				if (!response.ok) {
					// Try to extract error details from response body
					let errorMessage = `HTTP error! status: ${response.status}`;
					let errorData: any = null;

					try {
						// Try to read error response body
						const contentType =
							response.headers.get('content-type');
						if (
							contentType &&
							contentType.includes('application/json')
						) {
							errorData = await response.json();
							errorMessage =
								errorData?.message ||
								errorData?.error ||
								errorData?.detail ||
								`HTTP error! status: ${response.status}`;
						} else {
							const text = await response.text();
							if (text) {
								errorMessage =
									text.length > 200
										? text.substring(0, 200)
										: text;
							}
						}
					} catch (parseError) {
						// If we can't parse the error, use the status code
						if (__DEV__) {
							console.warn(
								'Could not parse error response:',
								parseError
							);
						}
					}

					// Create a proper error object with status information
					const error: any = new Error(errorMessage);
					error.status = response.status;
					error.statusText = response.statusText;
					error.data = errorData;
					throw error;
				}

				// WEB-SPECIFIC: Check if response is streaming by content-type or body
				const contentType = response.headers.get('content-type') || '';
				const hasBody = response.body !== null;
				const isStreaming = 
					contentType.includes('text/event-stream') ||
					contentType.includes('application/x-ndjson') ||
					contentType.includes('text/plain') ||
					(requestPayload.stream && hasBody);

				if (isStreaming && hasBody) {
					if (__DEV__) {
						console.log('📥 [handleHelp.web] Returning streaming response', {
							contentType,
							hasBody,
						});
					}
					// Return the streaming response directly
					return response;
				}

				// Fallback: Parse non-streaming response
				const responseData = await response.json();
				
				let responseText = '';

				// Log raw response for debugging
				if (__DEV__) {
					console.log('📥 [handleHelp] Raw response:', {
						status: response.status,
						hasData: !!responseData,
						dataType: typeof responseData,
						dataKeys: responseData ? Object.keys(responseData) : [],
						dataPreview: responseData
							? JSON.stringify(responseData).substring(0, 200)
							: 'null',
					});
				}

				// Handle different response formats
				if (typeof responseData === 'string') {
					responseText = responseData;
				} else if (responseData?.response) {
					// Backend returns { response: "text", ... }
					responseText =
						typeof responseData.response === 'string'
							? responseData.response
							: responseData.response
								? JSON.stringify(responseData.response)
								: '';
				} else if (responseData?.text) {
					responseText =
						typeof responseData.text === 'string'
							? responseData.text
							: responseData.text
								? JSON.stringify(responseData.text)
								: '';
				} else if (responseData) {
					// Fallback: stringify the whole response, but check if it's meaningful
					const stringified = JSON.stringify(responseData);
					// Only use if it's not just "{}" or "null"
					if (
						stringified &&
						stringified !== '{}' &&
						stringified !== 'null' &&
						stringified !== '""'
					) {
						responseText = stringified;
					}
				}

				// Log response details for debugging
				if (__DEV__) {
					console.log('📥 [handleHelp] Extracted response text:', {
						hasData: !!responseData,
						hasResponse: !!responseData?.response,
						responseType: typeof responseData?.response,
						responseLength: responseText.length,
						responsePreview: responseText.substring(0, 100),
						isEmpty:
							!responseText || responseText.trim().length === 0,
					});
				}

				// If responseText is empty, provide a default error message
				if (!responseText || responseText.trim().length === 0) {
					console.warn(
						'⚠️ [handleHelp] Empty response from backend:',
						{
							responseData,
							responseStatus: response.status,
							usingFallback: true,
						}
					);
					responseText =
						'I apologize, but I received an empty response from the AI service. This might be due to a connection issue or the service being temporarily unavailable. Please try rephrasing your question or try again in a moment.';
				}

				// Create a mock Response object with getReader() for streaming compatibility
				// HelpModal expects chunks in format: {"response": "text chunk"}
				const mockResponse = {
					ok: response.status >= 200 && response.status < 300,
					status: response.status,
					statusText: response.statusText || 'OK',
					body: {
						getReader: () => {
							// Create a reader that streams the response as JSON chunks
							let position = 0;
							const encoder = new TextEncoder();
							const chunks: Uint8Array[] = [];

							// Split response into chunks to simulate streaming
							// Each chunk is formatted as: {"response": "chunk text"}
							const chunkSize = 30; // characters per chunk for smoother streaming
							for (
								let i = 0;
								i < responseText.length;
								i += chunkSize
							) {
								const chunk = responseText.substring(
									i,
									i + chunkSize
								);
								const jsonChunk = JSON.stringify({
									response: chunk,
								});
								chunks.push(encoder.encode(jsonChunk));
							}

							// If no chunks were created (empty response), add at least one empty chunk
							if (chunks.length === 0) {
								chunks.push(
									encoder.encode(
										JSON.stringify({ response: '' })
									)
								);
							}

							return {
								read: async () => {
									if (position >= chunks.length) {
										return { done: true, value: undefined };
									}
									const chunk = chunks[position];
									position++;
									return {
										done: false,
										value: chunk,
									};
								},
							};
						},
					},
				};

				return mockResponse as any;
			} catch (fetchError: any) {
				if (__DEV__) {
					console.error('❌ AI Help request failed:', fetchError);
					console.log('Error details:', {
						message: fetchError?.message,
						status: fetchError?.status,
						statusText: fetchError?.statusText,
						data: fetchError?.data,
					});
				}

				// Handle fetch errors (fetch errors have status directly on error object)
				if (fetchError?.status) {
					const status = fetchError.status;
					const errorData = fetchError.data;

					if (status === 401 || status === 403) {
						throw new Error(
							'Authentication failed. Please log in again.'
						);
					} else if (status === 500) {
						const errorMsg =
							errorData?.message ||
							errorData?.error ||
							errorData?.detail ||
							'Server error occurred. The AI service may be temporarily unavailable. Please try again in a moment.';
						throw new Error(`Server error: ${errorMsg}`);
					} else if (status === 503) {
						throw new Error(
							'Service temporarily unavailable. Please try again in a moment.'
						);
					} else if (status === 429) {
						throw new Error(
							'Too many requests. Please wait a moment before trying again.'
						);
					} else {
						const errorMsg =
							errorData?.message ||
							errorData?.error ||
							errorData?.detail ||
							`HTTP error! status: ${status}`;
						throw new Error(errorMsg);
					}
				}

				// Handle network errors or other fetch failures
				if (fetchError?.message) {
					// Check if it's a network error
					if (
						fetchError.message.includes('Failed to fetch') ||
						fetchError.message.includes('NetworkError') ||
						fetchError.message.includes('Network request failed')
					) {
						throw new Error(
							'Network error: Unable to connect to the AI service. Please check your internet connection and try again.'
						);
					}
					throw new Error(fetchError.message);
				}

				throw new Error(
					'Failed to get response from AI help endpoint. Please check your connection and try again.'
				);
			}
		} else {
			// For editor-connected content generation
			// Use the generate endpoint with conversation history support
			const generatePayload = {
				q: query, // genAi endpoint expects 'q' parameter
				conversation_history: formattedHistory,
				context: requestPayload.context,
			};

			try {
				const response = await noMonitor
					.post('/ai/generate', generatePayload)
					.then((res) => res.data);

				if (!response) {
					// If POST fails, try the old GET method as fallback
					if (response.status === 404 || response.status === 405) {
						if (__DEV__) {
							console.log(
								'New API format not available, falling back to old format'
							);
						}
						const fallbackResponse = await fetch(
							`${HOST}/api/ai/generate?q=${encodeURIComponent(query)}`,
							{
								method: 'GET',
								headers: {
									'Content-Type': 'application/json',
								},
							}
						);

						if (!fallbackResponse.ok) {
							throw new Error(
								`HTTP error! status: ${fallbackResponse.status}`
							);
						}

						return await fallbackResponse.json();
					}
					throw new Error(`HTTP error! status: ${response.status}`);
				}

				return response;
			} catch (fetchError) {
				if (__DEV__) {
					console.log(
						'POST request failed, trying GET fallback:',
						fetchError
					);
				}
				// Fallback to old GET method
				const fallbackResponse = await fetch(
					`${HOST}/api/ai/generate?q=${encodeURIComponent(query)}`,
					{
						method: 'GET',
						headers: {
							'Content-Type': 'application/json',
						},
					}
				);

				if (!fallbackResponse.ok) {
					throw new Error(
						`HTTP error! status: ${fallbackResponse.status}`
					);
				}

				// WEB-SPECIFIC: Check if response is streaming before trying to parse as JSON
				const contentType = fallbackResponse.headers.get('content-type') || '';
				const isStreaming = 
					contentType.includes('text/event-stream') ||
					contentType.includes('application/x-ndjson') ||
					contentType.includes('text/plain') ||
					(fallbackResponse.body !== null);

				if (isStreaming && fallbackResponse.body) {
					if (__DEV__) {
						console.log('📥 [handleHelp.web] Fallback response is streaming, returning response object');
					}
					// Return the streaming response directly
					return fallbackResponse;
				}

				// Only parse as JSON if it's not streaming
				return await fallbackResponse.json();
			}
		}
	} catch (e) {
		console.error('AI Help Error:', e);
		throw new Error(
			'Failed to get response from AI: ' + (e as Error).message
		);
	}
}

// Utility functions for history management
export function createChatMessage(
	role: 'user' | 'assistant',
	content: string
): ChatMessage {
	return {
		role,
		content: content.trim(),
		timestamp: new Date(),
	};
}

export function convertGiftedChatToHistory(
	giftedMessages: any[]
): ChatMessage[] {
	// Reduced logging for better performance - only log in development
	if (__DEV__) {
		console.log(
			'🔄 convertGiftedChatToHistory - Input messages:',
			giftedMessages.length
		);
	}

	if (!giftedMessages || giftedMessages.length === 0) {
		if (__DEV__) {
			console.log('🔄 No messages to convert');
		}
		return [];
	}

	const result = giftedMessages
		.filter((msg) => {
			const hasText = msg.text && msg.text.trim().length > 0;
			if (!hasText && __DEV__) {
				console.log('🔄 Filtering out message without text:', msg);
			}
			return hasText;
		})
		.map((msg) => {
			// Check if it's a system message OR if user._id is "AI" OR if it has system: true
			const isSystemMessage =
				msg.system ||
				(msg.user && (msg.user._id === 'AI' || msg.user.system));
			const role: 'user' | 'assistant' = isSystemMessage
				? 'assistant'
				: 'user';

			const result = {
				role,
				content: msg.text.trim(),
				timestamp: msg.createdAt ? new Date(msg.createdAt) : new Date(),
			};

			if (__DEV__) {
				console.log('🔄 Converted message:', {
					text: msg.text.substring(0, 50) + '...',
					system: msg.system,
					userID: msg.user?._id,
					role: result.role,
				});
			}
			return result;
		})
		.reverse() // GiftedChat stores messages in reverse order
		.slice(-20); // Keep last 20 messages for context

	if (__DEV__) {
		console.log('🔄 Final conversion result:', result.length, 'messages');
	}
	return result;
}

export function sanitizeHistoryForAPI(history: ChatMessage[]): ChatMessage[] {
	const result = history
		.filter((msg) => msg.content && msg.content.trim().length > 0)
		.map((msg) => ({
			role: msg.role,
			content: msg.content.replace(/<think>.*?<\/think>/gs, '').trim(), // Remove thinking tags if any
		}))
		.slice(-10); // Limit to last 10 messages to avoid token limits

	// Reduced logging for better performance - only log in development
	if (__DEV__) {
		console.log(
			'🧹 sanitizeHistoryForAPI - Input:',
			history.length,
			'Output:',
			result.length
		);
	}
	return result;
}
